import Abstract from './Mix/Abstract.es6';

class Mix extends Abstract {
    /**
     * Initializes a new Mix model.
     *
     * @param plainObject The plain json (optional).
     */
    constructor(plainObject) {
        super({
            // TODO User Sets here.
            recipe: {},
            notifications: new Set(),
            base: null,
            name: ''
        }, plainObject);

        /* Configuration */
        this._isInitialized         = true;

        // Define vars
        this.KILO                   = 1000;
        this.QUOTATION_VALUE        = this.KILO;
    }

    /**
     * A helper function for getting a list of all available ingredients.
     *
     * @returns {string|*}
     * @private
     */
    _allIngredients() {
        return App.Components.Mixer.Instance.indexedIngredients;
    };

    /**
     * Gets the ingredient object for an ingredient's id. The ingredient must not be part of the mix or an
     * available ingredient!
     *
     * Helper function for internal and external use.
     *
     * @param ingredientID The id of the ingredient.
     * @returns {mymuesli.entities.Ingredient} The ingredient.
     */
    getIngredient (ingredientID) {
        return this._allIngredients()[ingredientID];
    }

    /**
     * A helper function for getting the name of a numeric type.
     *
     * @param ingredientID The id of the ingredient.
     * @returns {string} The name of the type.
     * @private
     */
    _typeName(ingredientID) {
        return this._allIngredients()[ingredientID].typeName;
    };

    /**
     * Check if an ingredient id still exists.
     *
     * @param ingredientID The id of the ingredient.
     * @returns {boolean} True if the ingredient exists.
     */
    hasIngredient(ingredientID) {
        return (this.getIngredient(ingredientID) !== undefined);
    }

    /**
     * Gets the amount of the ingredient in the current recipe.
     *
     * @param ingredientID The id of the ingredient.
     * @returns integer The amount of the ingredient in the recipe.
     */
    getAmount(ingredientID) {
        ingredientID = this.encodeId(ingredientID);
        return (this.recipe[ingredientID] || 0);
    }

    /**
     * Adds an ingredient to the mix. If the ingredient cannot be added an exception is thrown.
     *
     * @param ingredientID The id of the ingredient.
     * @param amount The number of times the ingredient should be added. This must be greater 0.
     * @param weakCheck Default false. If set to true error messages / handling will be suppressed (predefined mixes without base!).
     * @throws mymuesli.entities.MixError Thrown if an ingredient constraint is violated or the tube if full.
     * @return Mix The object itself for chaining.
     */
    addIngredient(ingredientID, amount, weakCheck = false, notify = true) {
        ingredientID = parseInt(ingredientID);

        /* Check if the ingredient is sold out. */
        if (!this._checkIfIsActiveIngredient(ingredientID, weakCheck)) {
            return this;
        }

        ingredientID = this.encodeId(ingredientID);
        /* Try to add the ingredient as often as desired. This can cause MixErrors. */
        for (let i = 0; i < amount; i++) {
            if (weakCheck || this.canAddIngredient(ingredientID, 1)) {
                if (this.recipe[ingredientID] === undefined) {
                    this.recipe[ingredientID] = 0;
                }
                this.recipe[ingredientID]++;
            }
        }
        if (notify) {
            this.notify();
        }
        return this;
    }



    /**
     * encodeId
     *
     * Generates a string so that javascript do not sort them again
     *
     * @param id integer The id of the ingredient.
     * @returns string id with a suffix
     */
    encodeId(id) {
        if (id) {
            return '_' + this.decodeId(id);
        }
    }


    /**
     * decodeId
     *
     * Get int is from encoded ID
     *
     * @param id string encoded ID
     * @returns int ID
     */
    decodeId(id) {
        if (id === parseInt(id, 10)) return id;
        return id.match(/\d+/)[0];
    }

    /**
     * Check if an ingredient can be added one time.
     *
     * @param ingredientID integer The id of the ingredient.
     * @param amount integer The number of times the ingredient should be added.
     * @returns {boolean} true if everything is fine, otherwise false.
     */
    canAddIngredient (ingredientID, amount) {
        ingredientID = this.decodeId(ingredientID);

        /* Validate the constraints for the ingredient. */
        this._validateConstraintsForIngredient(ingredientID, amount);
        const ingredient = this.getIngredient(ingredientID);

        /* Check if the minimum weight of the base is not violated by calling
        currentBaseWeight with the ingredient to be added.
        If weight thresholds are exceeded, an exception will be raised */
        this.getCurrentBaseWeight(ingredient, amount)

        // if no exception is raised, we can add the ingredient.
        //
        // EVEN if the current mix is currently(!) not possible, adding a new "heavy" ingredient
        // can lead to a valid mix (i.e. which meets the volume/weight constraints)

        // Example: 3x raspberries and 3x strawberries is not possible, (342g total weight -> too low)
        // but 3x raspberries and 3x strawberries + 1 cashew is possible (351g total weight -> good to go).
        return true;

    }

    /**
     * Removes an ingredient from the mix.
     *
     * @param ingredientID integer The id of the ingredient.
     * @param amount integer The number of times the ingredient should be added.
     * @return Mix The object itself for chaining.
     */
    removeIngredient (id, amount) {
        let ingredientID    = this.decodeId(id);
        let ingredient      = this.getIngredient(ingredientID);

        if (ingredient.isBase) {
            this.setBase(null);
            return null;
        } else {

            /* Remove the ingredient completely from the recipe or subtract. */
            if (amount === null) {
                this.recipe[this.encodeId(ingredientID)] = 0;
            } else {
                this.recipe[this.encodeId(ingredientID)] -= amount;
            }

            /* Check if anything is left of the ingredient. */
            if (this.recipe[this.encodeId(ingredientID)] <= 0) {
                delete this.recipe[this.encodeId(ingredientID)];
            }

            this.notify();

            return this.recipe[this.encodeId(ingredientID)];
        }
    }

    /**
     * Returns the id of the base or null.
     *
     * @returns {null|integer}
     */
    getBase() {
        return parseInt(this.base);
    }

    /**
     * Checks if a base has been set yet.
     *
     * @returns boolean true if a base is set.
     */
    hasBase() {
        return (this.base !== null);
    }

    /**
     * Sets the base.
     *
     * @param ingredientID int The id of the ingredient to add.
     * @param weakCheck boolean True if errors are skipped silent.
     * @return Mix The object itself for chaining.
     */
    setBase(ingredientID, weakCheck = false, notify = true) {
        if (!this._checkIfIsActiveIngredient(ingredientID, weakCheck)) {
            return this;
        }
        this._validateConstraintsForIngredient(ingredientID, 1);
        this.base = ingredientID;
        if (notify) {
            this.notify();
        }
        return this;
    }

    /**
     * Set a whole recipe. The recipe is not validated in this step.
     *
     * @param recipe The recipe as list of {id:integer,amount:integer} objects.
     * @return Mix The object itself for chaining.
     */
    setRecipe(recipe) {
        const self = this;
        $.each(recipe, function (_, ingredient) {
            if (self.hasIngredient(ingredient.id)) {
                self.getIngredient(ingredient.id).isBase ? self.setBase(ingredient.id, true, false) : self.addIngredient(ingredient.id, ingredient.amount, true, false);
            }
        });
        return this;
    }

    /**
     * Get the price of the mix as float.
     *
     * @returns {number}
     */
    getPrice () {
        const self = this;
        let price = this.getIngredient(this.getBase()).portionPrice;

        /* Add all ingredient prices to the price of the base. */
        for (var ingredientID in this.getRecipe()) {
            ingredientID = this.decodeId(ingredientID);
            price += (self.getAmount(ingredientID) * self.getIngredient(ingredientID).portionPrice);
        }
        return price;
    }

    /**
     * Get the price per 1kg as float.
     *
     * @returns {number} The price per 1kg.
     */
    getPriceQuotation () {
        return (this.getPrice() / (this.getTotalWeight() / this.KILO)) * this.QUOTATION_VALUE;
    }

    /**
     * Get the nutrition table. The table is an object where the key is the nutrition property and the value
     * is the corresponding value.
     *
     * @returns {{}} The nutrition table.
     */
    getNutrition () {
        let nutrition = {};

        /* Initialize the nutrition with the base. */
        for (var type in this.getIngredient(this.getBase()).nutrition) {
            let value = this.getIngredient(this.getBase()).nutrition[type];
            let currentBaseWeight = 0;
            try {
                currentBaseWeight = this.getCurrentBaseWeight();
            } catch (e) {
                // if mix is not possible, exception will be thrown, base weight stays 0
            }
            nutrition[type] = (currentBaseWeight / this.getTotalWeight()) * value;
        }

        /* Add the nutrition values of the ingredients. */
        for (var ingredientID in this.getRecipe()) {
            ingredientID = this.decodeId(ingredientID);
            for (var type in this.getIngredient(ingredientID).nutrition) {
                let value = this.getIngredient(ingredientID).nutrition[type];
                nutrition[type] += ((this.getIngredient(ingredientID).portionMilligram * this.getAmount(ingredientID)) / this.getTotalWeight()) * value;
            }
        }
        return nutrition;
    }

    /**
     * Get the total weight of the tube. TODO!
     *
     * @returns {number}
     */
    getTotalWeight () {
        if (!this.hasBase()) {
            return 0.0;
        }
        return this.getIngredient(this.getBase()).portionMilligram;
    }

    /**
     * Get the maximum weight of the ingredients without the base.
     *
     * @returns {number} The maximum ingredients wight.
     */
    getMaximumIngredientWeight () {
        return this.getTotalWeight();
    }

    /**
     * Get the current weight of all ingredients without the base.
     *
     * @returns {number} The pure ingredients weight without the base.
     */
    getCurrentIngredientsWeight () {
        const self = this;
        let weight = 0.0;

        for (var ingredientID in this.getRecipe()) {
            ingredientID = this.decodeId(ingredientID);
            weight += (self.getAmount(ingredientID) * self.getIngredient(ingredientID).portionMilligram);
        }
        return weight;
    }

    countIngredients() {
        let count = this.hasBase() ? 1 : 0 ;
        count += Object.keys(this.getRecipe()).length;
        return count;
    }

    /**
     * Gets the current weight of the base. That's the weight of the whole tube subtracted the weight of the
     * ingredients. If the target volume is breached, we reduce the base weight until the target volume can
     * be met. If the base weight or mix total weight is below the min thresholds, we raise an exception to
     * indicate that the mix is not possible
     *
     * Achtung: wenn die Logik hier geändert wird, auch im BE (CustomMixCalculator::calculateWeights) anpassen!
     *
     * @returns {number}
     */
    getCurrentBaseWeight(ingredientToAdd, amountToAdd) {
        let totalIngredientsWeight = 0;
        let totalIngredientsVolume = 0;
        const maxVolume = this.getTotalVolume(); // 1495 ccm * 1000

        const targetWeight = this.getTotalWeight(); // mg
        if (targetWeight <= 0) {
            throw new mymuesli.entities.MixError('mixer.ERROR_MESSAGE_NO_BASE');
        }

        const minTargetWeight = this.getMinimumOverallMixWeight(); // mg
        const minBaseWeight =  this.getMinimumBaseWeight(); // mg

        /* The volume of the total ingredients. */
        for (let ingredientID in this.getRecipe()) {
            ingredientID = this.decodeId(ingredientID);

            totalIngredientsVolume += (this.getAmount(ingredientID) * this.getIngredient(ingredientID).volume);
            totalIngredientsWeight += (this.getAmount(ingredientID) * this.getIngredient(ingredientID).portionMilligram);
        }

        // only for the case if we want check if a new ingredient can be added
        if (ingredientToAdd !== undefined) {
            totalIngredientsVolume += amountToAdd * ingredientToAdd.volume;
            totalIngredientsWeight += amountToAdd * ingredientToAdd.portionMilligram;
        }

        // calculate the weight of the base ingredient needed to reach target weight
        let baseWeight = targetWeight - totalIngredientsWeight;

        const baseDensity = this.getIngredient(this.getBase()).density;
        // calculate the total volume with base ingredient
        let totalVolumeWithBase = (baseWeight / baseDensity) + totalIngredientsVolume;

        // check if the total volume with base ingredient exceeds the maximum allowed
        if (totalVolumeWithBase > maxVolume) {
            // reduce the base weight by the amount that must be removed to fit into the max_volume
            let reducedWeight = (totalVolumeWithBase - maxVolume) * baseDensity;
            baseWeight -= reducedWeight;
            // total weight + base weight meets minTargetWeight?
            if (totalIngredientsWeight + baseWeight < minTargetWeight) {
                throw new mymuesli.entities.MixError('mixer.ERROR_MESSAGE_NO_SPACE');
            }
        }

        // check if the base weight never falls below 125 g
        if (baseWeight < minBaseWeight) {
            throw new mymuesli.entities.MixError('mixer.ERROR_MESSAGE_NO_SPACE');
        }
        
        return baseWeight;
    }

    /**
     * Get the total volume of the tube.
     *
     * @returns {number} The total volume.
     */
    getTotalVolume() {
        return this.volume * this.KILO; // Tins Volume in milligram
    }

    /**
     *
     * @returns {number} the minimum weight for a custom mix in mg
     */
    getMinimumOverallMixWeight() {
       return this.minOverallWeight * this.KILO;
    }

    /**
     *
     * @returns {number} the minimum weight of the basis in mg
     */
    getMinimumBaseWeight() {
        return this.minWeightBase * this.KILO;
    }

    /**
     * Get the current volume of the base and the ingredients.
     *
     * @returns {number} The current volume.
     */
    getCurrentVolume () {
        const self = this;

        /* The volume of the base. */
        let volume = 0.0;
        if (self.hasBase()) {
            let currentBaseWeight = 0;
            try {
                /**
                 * getCurrentBaseWeight might throw an exception if mix is not possible
                 */
                currentBaseWeight = this.getCurrentBaseWeight();
            } catch (e) {} // base weight stays 0 if mix is not possible
            volume = currentBaseWeight / self.getIngredient(self.getBase()).density;
        }

        /* The volume of the total ingredients. */
        for (var ingredientID in this.getRecipe()) {
            ingredientID = this.decodeId(ingredientID);
            volume += (self.getAmount(ingredientID) * self.getIngredient(ingredientID).volume);
        }
        return volume;
    }

    /**
     * Get the recipe of the current mix (without the base). The ingredients a grouped by type. For each type
     * the recipe is a dictionary where the key is the ingredient's id and the value is the amount.
     *
     * @returns {{refine: {}, fruits: {}, nuts: {}, extras: {}}} The recipe.
     */
    getRecipe() {
        return this.recipe;
    }

    /**
     * prepare recipe for API
     */
    getRecipeForAPI() {
        let i       = 0;
        let recipe  = {};
        recipe[i++] = {
            id: this.getBase(),
            amount: 1,
        };
        for (let ingredient in this.recipe) {
            recipe[i++] = {
                id: this.decodeId(ingredient),
                amount: this.recipe[ingredient],
            };
        }
        return recipe;
    }

    /**
     * Get the recipe as list with {id:int,amount:int} entries.
     *
     * @returns {[{id: int, amount: int}]}
     */
    getRecipeAsList () {
        const self = this;
        const recipeList = [];
        if (this.hasBase()) {
            recipeList.push({id: this.getBase(), amount: 1});
        }
        for (var ingredientID in this.getRecipe()) {
            ingredientID = this.decodeId(ingredientID);
            recipeList.push({id: ingredientID, amount: self.getAmount(ingredientID)});
        }
        return recipeList;
    }

    /**
     * Get the recipe as list with ingredient entities.
     *
     * @return {mymuesli.entities.Ingredient[]} The ingredients list.
     */
    getRecipeAsIngredientList () {
        const self = this;

        /* Collect the recipe as ingredients list without amounts. */
        let ingredients = [];
        for (var ingredientID in this.getRecipe()) {
            ingredientID = this.decodeId(ingredientID);
            ingredients.push(self.getIngredient(ingredientID));
        }
        return ingredients;
    }

    async getRecipeFormatted() {
        const item = async (ingredient, amount) => {
            const types = Mixer.Instance.ingredients.types;
            return {
                id:         ingredient.id,
                name:       ingredient.name,
                image:      ingredient.icon.path,
                type:       ingredient.typeName,
                color:      types[ingredient.typeName].mixerBackgroundColor,
                density:    ingredient.density * amount,
                amount:     amount,
                weight:     ingredient.portionMilligram * amount / 1000,
                isBase:     ingredient.isBase,
            }
        }
        let base = [];
        const density = {};
        if (this.getBase()) {
            const id = this.decodeId(this.getBase());
            const ingredient = await App.Endpoint.Ingredients.getById(id);
            base = [await item(ingredient,1)];
            density[this.decodeId(id)] = ingredient.density * 1;
        }
        let values = [];
        let baseWeight = 0;
        for (const id in this.getRecipe()) {
            const ingredient = await App.Endpoint.Ingredients.getById(this.decodeId(id));
            values.push(await item(ingredient, this.getRecipe()[id]));
            density[this.decodeId(id)] = ingredient.density * this.getRecipe()[id];
            baseWeight = baseWeight + ingredient.portionMilligram * this.getRecipe()[id];
        }
        // Calculate base gram weight
        if (base.length > 0) base[0].weight = (this.getTotalWeight() - baseWeight) / 1000;

        values = [...values, ...base];
        const sum = Object.values(density).reduce((acc, val) => acc + val, 0);
        const percentages = Object.fromEntries(
            Object.entries(density).map(([key, value]) => [key, (value / sum * 100).toFixed(2) + '%'])
        );
        for (const ingredient of values) ingredient.volume = percentages[ingredient.id];
        return values;
    }

    /**
     * Returns a list of active notification texts.
     *
     * @returns {Array}
     */
    getNotificationsAsList () {
        return Array.from(this.notifications);
    }

    /**
     * Get a list of recommended ingredients for the current mix. The list is updated asynchronous and will use
     * the notification-mechanism.
     *
     * @returns [integer] A list of ingredient ids the user probably likes.
     */
    getRecommendations () {
        return [];
    }

    /**
     * Returns weather ingredients still can be added or not.
     *
     * @returns {boolean} True if no more ingredients can be added.
     */
    isTooFull () {

        if (this.getCurrentBaseWeight() < this.getMinimumBaseWeight()) {
            return true;
        }

        return false;
    }

    /**
     * Checks if the current mix is valid or not. For getting detailed error messages use validate().
     *
     * @return boolean True if the mix is valid, otherwise false.
     */
    isValid () {
        try {
            this.validate();
            return true;
        } catch (e) {
            return false;
        }
    }

    /**
     * Checks if the current mix needs a basis reduction, because 575g will not fit in the container volume
     *
     * @return boolean True if the mix will have a weight reduction
     */
    isMerge() {
        try {
            // if the sum of the base weight and ingredient sum is lower than the specified weight for this basis
            return ((this.getCurrentBaseWeight() + this.getCurrentIngredientsWeight()) < this.getTotalWeight());
        } catch (e) {
            return false;
        }
    }

    /**
     * Validates the current mix.
     *
     * @throws Error the identifier of the localised error message string.
     * @return Mix The object itself for chaining.
     */
    validate () {

        /* Check if we got a base. */
        if (!this.hasBase()) {
            throw new mymuesli.entities.MixError('mixer.ERROR_MESSAGE_NO_BASE');
        }

        /* The tube is already full. This can happen if the base has been switched. */
        if (this.isTooFull()) {
            throw new mymuesli.entities.MixError('mixer.ERROR_MESSAGE_NO_SPACE');
        }
        
        /* volume & weight if base changed */
        return this;
    }

    /**
     * Reset the mix.
     *
     * @return Mix The object itself for chaining.
     */
    clear() {
        this._initializeDefaults({
            recipe: {},
            notifications: new Set(),
            base: null,
            name: ''
        });
        this.notify();
        return this;
    }

    /**
     * Triggers a re-validation of all constraints, e.g. to trigger notification texts before the
     * listeners are notified.
     *
     * Suppress notify() if the entity is still in the bootstrapping process. Maybe we should move this
     * to the parent class.
     */
    notify() {
        if (this._isInitialized) {
            this._revalidateAllConstraints();
            super.notify();
        }
    }

    /**
     * Check if an ingredient is available (active and not sold out).
     *
     * @param ingredientID The id of the ingredient to check.
     * @param weakCheck boolean If weak checks are enabled the adding will be skipped without notification.
     * @return boolean
     * @private
     */
    _checkIfIsActiveIngredient (ingredientID, weakCheck) {
        if (ingredientID === null) {
            return true;
        }

        /* If the ingredient is not available it cannot be added to the mix. */
        if (!this.getIngredient(ingredientID).isActive && !weakCheck) {
            throw new mymuesli.entities.MixError('mixer.ERROR_MESSAGE_INGREDIENT_UNAVAILABLE');
        }
        return this.getIngredient(ingredientID).isActive;
    }

    /**
     * Checks if the constraints to add the ingredient are fulfilled.
     *
     * @param ingredientID The id of the ingredient to check.
     * @param amount The amount (change) of the ingredient to add.
     * @private
     */
    _validateConstraintsForIngredient (ingredientID, amount) {
        try {
            this.getIngredient(ingredientID).validateConstraints(this, amount);
        } catch (error) {
            if (error instanceof mymuesli.entities.IngredientError) {
                throw new mymuesli.entities.MixError(error.message, error.parameters);
            }
        }
    }

    /**
     * Re-validate all other constraints. E.g. for amount dependencies. This step may also update the current state.
     *
     * @private
     */
    _revalidateAllConstraints () {
        this.notifications.clear();
        const self = this;
        $.each(this.getRecipeAsList(), function (_, ingredient) {
            self._validateConstraintsForIngredient(ingredient.id, 0);
        });
    }
}

App.Entities.Mix = Mix;
